import React, { useState, useCallback, useEffect } from 'react';
import { Upload, Download, FileText, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { useAppointments } from '../../hooks/useAppointments';
import { supabase } from '../../lib/supabase';

interface UploadRecord {
  id: string;
  file_name: string;
  record_count: number;
  created_at: string;
}

const formatRelativeTime = (isoDate: string): string => {
  const diff = Date.now() - new Date(isoDate).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days !== 1 ? 's' : ''} ago`;
};

export const DataUpload: React.FC = () => {
  const { uploadData } = useAppointments();
  const [dragActive, setDragActive] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle');
  const [uploadMessage, setUploadMessage] = useState('');
  const [history, setHistory] = useState<UploadRecord[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    const { data, error } = await supabase
      .from('upload_history')
      .select('id, file_name, record_count, created_at')
      .order('created_at', { ascending: false })
      .limit(10);
    if (!error && data) setHistory(data);
    setHistoryLoading(false);
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) await handleFileUpload(files[0]);
  }, []);

  const handleFileUpload = async (file: File) => {
    if (!file) return;

    const validTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    if (
      !validTypes.includes(file.type) &&
      !file.name.endsWith('.csv') &&
      !file.name.endsWith('.xlsx') &&
      !file.name.endsWith('.xls')
    ) {
      setUploadStatus('error');
      setUploadMessage('Please upload a CSV or Excel file.');
      return;
    }

    setUploadStatus('uploading');
    setUploadMessage('Processing your file...');

    try {
      const success = await uploadData(file);
      if (success) {
        setUploadStatus('success');
        setUploadMessage(`Successfully uploaded ${file.name}. Data has been processed and predictions updated.`);
        await fetchHistory();
      } else {
        setUploadStatus('error');
        setUploadMessage('Failed to process the file. Please check the format and try again.');
      }
    } catch {
      setUploadStatus('error');
      setUploadMessage('An error occurred while uploading the file.');
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files[0]) handleFileUpload(files[0]);
  };

  const downloadTemplate = () => {
    const csvContent = `Patient ID,Age Group,Appointment Date,Appointment Time,Visit Type,Department,Location,Historical Attendance,Patient Email,Patient Phone,Preferred Contact Method,Email Consent,SMS Consent
pat-001,25-35,2024-02-15,09:30,Consultation,Cardiology,Main Hospital,80,john.doe@email.com,+1234567890,both,true,true
pat-002,45-55,2024-02-15,10:00,Follow-up,Orthopedics,North Clinic,95,jane.smith@email.com,+1234567891,email,true,false
pat-003,35-45,2024-02-15,10:30,Procedure,Neurology,Main Hospital,60,bob.johnson@email.com,+1234567892,sms,false,true`;

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'appointment_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Data Upload</h1>
        <p className="text-gray-600 mt-2">Import appointment data for AI analysis and predictions</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg border p-8">
            <div
              className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
                dragActive ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <Upload className="w-8 h-8 text-gray-600" />
              </div>

              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Drop your files here, or click to browse
              </h3>

              <p className="text-gray-600 mb-6">Supports CSV and Excel files up to 10MB</p>

              <input
                type="file"
                id="file-upload"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileInput}
                className="hidden"
                disabled={uploadStatus === 'uploading'}
              />

              <label
                htmlFor="file-upload"
                className={`inline-flex items-center px-6 py-3 border border-transparent rounded-md font-medium cursor-pointer transition-colors ${
                  uploadStatus === 'uploading'
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {uploadStatus === 'uploading' ? (
                  <>
                    <Clock className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5 mr-2" />
                    Choose File
                  </>
                )}
              </label>
            </div>

            {uploadStatus !== 'idle' && (
              <div
                className={`mt-6 p-4 rounded-lg border flex items-start space-x-3 ${
                  uploadStatus === 'success'
                    ? 'bg-green-50 border-green-200'
                    : uploadStatus === 'error'
                    ? 'bg-red-50 border-red-200'
                    : 'bg-blue-50 border-blue-200'
                }`}
              >
                <div className="flex-shrink-0">
                  {uploadStatus === 'success' && <CheckCircle className="w-5 h-5 text-green-600" />}
                  {uploadStatus === 'error' && <AlertCircle className="w-5 h-5 text-red-600" />}
                  {uploadStatus === 'uploading' && <Clock className="w-5 h-5 text-blue-600 animate-spin" />}
                </div>
                <div className="flex-1">
                  <p
                    className={`font-medium ${
                      uploadStatus === 'success'
                        ? 'text-green-800'
                        : uploadStatus === 'error'
                        ? 'text-red-800'
                        : 'text-blue-800'
                    }`}
                  >
                    {uploadStatus === 'success' && 'Upload Successful'}
                    {uploadStatus === 'error' && 'Upload Failed'}
                    {uploadStatus === 'uploading' && 'Processing Upload'}
                  </p>
                  <p
                    className={`text-sm mt-1 ${
                      uploadStatus === 'success'
                        ? 'text-green-700'
                        : uploadStatus === 'error'
                        ? 'text-red-700'
                        : 'text-blue-700'
                    }`}
                  >
                    {uploadMessage}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-lg border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Data Requirements</h3>

            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-gray-700 mb-2">Required Fields:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Patient ID (anonymized)</li>
                  <li>• Appointment Date</li>
                  <li>• Appointment Time</li>
                  <li>• Visit Type</li>
                  <li>• Department</li>
                  <li>• Location</li>
                  <li>• Patient Email</li>
                  <li>• Patient Phone</li>
                </ul>
              </div>

              <div>
                <h4 className="font-medium text-gray-700 mb-2">Optional Fields:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Age Group</li>
                  <li>• Historical Attendance (%)</li>
                  <li>• Preferred Contact Method</li>
                  <li>• Email Consent</li>
                  <li>• SMS Consent</li>
                </ul>
              </div>
            </div>

            <button
              onClick={downloadTemplate}
              className="w-full mt-4 flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Template
            </button>
          </div>

          <div className="bg-white rounded-lg border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Upload History</h3>

            {historyLoading ? (
              <div className="space-y-3">
                {[1, 2].map(i => (
                  <div key={i} className="animate-pulse h-14 bg-gray-100 rounded-lg" />
                ))}
              </div>
            ) : history.length === 0 ? (
              <div className="text-center py-6">
                <FileText className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No uploads yet</p>
                <p className="text-xs text-gray-400 mt-1">Files you upload will appear here</p>
              </div>
            ) : (
              <div className="space-y-3">
                {history.map(record => (
                  <div key={record.id} className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg">
                    <FileText className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{record.file_name}</p>
                      <p className="text-xs text-gray-600">
                        {formatRelativeTime(record.created_at)} &bull; {record.record_count.toLocaleString()} records
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
